import React, { useRef, useState, useEffect } from "react";

import { useTheme } from '@mui/material/styles';
import { Box, Tooltip, Typography } from "@mui/material";

import { EVENTS_LOG, EVENTS_LOG_MAP, EVENTS_LOG_ERROR_TYPE } from "const/eventsLog";

import { getValueByPath } from "utils/getValueByPath";

const TableCellMessage = (theme, event, templates, fieldMap, onClick, mode = "table") => {
  const eventKey = getValueByPath(event, "payload.translationKey");
  let template = templates[eventKey];

  if (event.deviceType === "switch" &&
    (eventKey === "sw_port_error_recover") &&
    getValueByPath(event, "payload.errorType") === null) {
    template = template?.replace(". Error: <ErrorTypeString>", ".");
  }

  const mapRule = fieldMap[eventKey] || {};

  if (!template) return "-";

  const formattedData = template.split(/(<[^<>]+>|{[^{}]+})/g);
  const isLinkKey = (key) => /With.*Link$/.test(key);
  const isAcknowledge = event?.acknowledge === true;

  const renderObjectValue = (obj) => {
    const renderDeepValue = (v, depth = 1) => {
      const indent = "\u00A0\u00A0\u00A0\u00A0".repeat(depth); // 4 spaces per depth level
      if (v === null || v === undefined) return "";
      if (Array.isArray(v)) return v.join(", ");
      if (typeof v === "object") {
        return Object.entries(v)
          .map(([nk, nv]) => {
            const prefix = mode === "tooltip" ? "\n" : " ";
            return `${prefix}${indent}${nk}: ${renderDeepValue(nv, depth + 1)}`;
          })
          .join("");
      }
      return String(v);
    }

    const entries = Object.entries(obj);
    return entries.map(([keyItem, keyValue], i) => {
      const separator = mode === "tooltip" ? <br /> : i < entries.length - 1 ? ", " : null;
      const isValObject = typeof keyValue === "object" && keyValue !== null && !Array.isArray(keyValue);

      return (
        <span key={keyItem}>
          {keyItem}: {isValObject && mode === "tooltip" ? "" : ""}{renderDeepValue(keyValue)}
          {separator}
        </span>
      );
    });
  };

  return formattedData.map((item, index) => {
    const match = item.match(/^<(.+)>$|^{(.+)}$/);
    if (!match) {
      return <span key={index}>{item}</span>;
    }

    const key = match[1] || match[2];
    let path = mapRule[key];
    // translationKey 有 ap_client 的字串 (ClientNameWithMACAsLink)
    // 必須顯示 clientName
    // 如果 clientName 是 null 或空字串就用 macAddress 
    if (eventKey.includes("ap_client") && key === "ClientNameWithMACAsLink") {
      const clientName = getValueByPath(event, "payload.clientName");
      path = clientName ? "payload.clientName" : "payload.macAddress";
    }
    // 有值需要前端特別做轉換使用 translationValue
    let translationValue
    if (((eventKey.includes("sw_port_error")) && key === "ErrorTypeString")
      || ((eventKey.includes("sw_port_error_recover")) && key === "ErrorTypeString")) {
      const errorTypeValue = getValueByPath(event, "payload.errorType");
      translationValue = EVENTS_LOG_ERROR_TYPE[errorTypeValue]
    }

    const value = translationValue ? translationValue : getValueByPath(event, path);

    if (value === undefined) return <span key={index}>{item}</span>;

    const isObject = typeof value === "object" && value !== null;
    const display = isObject ? renderObjectValue(value) : (value === null || value === undefined ? "" : String(value));

    // 只有 With...Link 的變數才有點擊事件
    if (!isLinkKey(key) || isAcknowledge) {
      return <span key={index}>{display}</span>;
    }

    const modeColor = (mode === "tooltip" && theme.palette.primary.white) || (mode === "table" && theme.palette.primary.greyPrimary);

    return (
      <span
        key={index}
        style={{
          color: modeColor,
          cursor: "pointer",
          fontWeight: 500,
          textDecoration: "underline",
          transition: "color 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = theme.palette.primary.mainHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = modeColor;
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClick?.({ key, value, event });
        }}
      >
        {display}
      </span>
    );
  });
};

const EllipsisTooltipCell = ({ text, rowData, setDrawerOpen, setDrawerData }) => {
  const theme = useTheme();

  const textRef = useRef(null);
  const [isOverflow, setIsOverflow] = useState(false);

  // 處裡是否要顯示 Tooltip
  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflow(el.scrollWidth > el.clientWidth);
    }
  }, [text]);

  const messageNode = (mode) => TableCellMessage(
    theme,
    rowData,
    EVENTS_LOG,
    EVENTS_LOG_MAP,
    // key 字串的 key 值, value 字串代表的值, event 點擊的 table row 的全部資料
    ({ key, value, event }) => {
      setDrawerData({ key, ...event })
      setDrawerOpen(true)
    },
    mode
  );

  return (
    <>
      <Tooltip
        title={isOverflow && rowData.acknowledge !== true ?
          <Typography
            sx={{
              fontSize: theme.typography.customFontSize[1],
              fontWeight: theme.typography.customFontWeight[1],
              lineHeight: 1.54,
              color: theme.palette.primary.white,
              whiteSpace: "pre-line"
            }}>
            {messageNode("tooltip")}
          </Typography> : ""}
        disableHoverListener={!isOverflow}
        arrow
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 600,
            },
          },
        }}
      >
        <Box
          ref={textRef}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            cursor: isOverflow ? "pointer" : "default",
          }}
        >
          {messageNode("table") || "-"}
        </Box>
      </Tooltip>
    </>
  );
};

export default EllipsisTooltipCell;
